import React, { memo, useMemo, useCallback } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const DEFAULT_COLORS = ["#6c5ce7", "#ffeaa7"];

function DonutChart({
  data = [],
  colors = DEFAULT_COLORS,
  height = 180,
  title = "Verification Status",
  loadingCharts = true,
}) {
  const hasData = useMemo(() => {
    return data.length > 0 && data.some((item) => item.value > 0);
  }, [data]);

  const total = useMemo(() => {
    return data.reduce((sum, d) => sum + d.value, 0);
  }, [data]);

  const renderLabel = useCallback(
    ({ name, value, cx, cy, index }) => {
      if (!total) return null;

      const percent = ((value / total) * 100).toFixed(2);
      const positions = [
        { dx: -65, dy: -80 },
        { dx: 65, dy: 80 },
      ];
      const pos = positions[index] || { dx: 0, dy: 0 };

      return (
        <text
          x={cx + pos.dx}
          y={cy + pos.dy}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#222"
          fontSize={15}
          fontWeight="bold"
        >
          {name} {percent}%
        </text>
      );
    },
    [total],
  );


  return (
  <div className="bg-white rounded-xl shadow-md p-6">
    <p className="font-bold text-lg mb-4">{title}</p>
    <hr className="dashboard-divider" />

    {loadingCharts ? (
      <div className="w-full h-[60%] flex items-center justify-center bg-gray-50 border rounded-lg p-10 animate-pulse">
        <span className="text-gray-500 font-medium">Loading Chart...</span>
      </div>
    ) : !hasData ? (
      <div className="w-full h-[60%] flex items-center justify-center bg-gray-50 border rounded-lg p-10">
        <span className="text-gray-500 font-medium">No Data Available</span>
      </div>
    ) : (
      <>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              dataKey="value"
              nameKey="name"
              label={renderLabel}
              labelLine={false}
            >
              {data.map((_, index) => (
                <Cell key={index} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>

        <div className="flex justify-center gap-4 mt-2">
          <LegendItem label={data[0]?.name || null} color={colors[0]} />
          <LegendItem label={data[1]?.name || null} color={colors[1]} />
        </div>
      </>
    )}
  </div>
);
}

const LegendItem = memo(function LegendItem({ label, color }) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
      <span
        className="inline-block w-3.5 h-3.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </div>
  );
});

export default memo(DonutChart);
